// src/components/pcr/FormControls.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from '../../contexts/FormContext';
import { jsPDF } from 'jspdf';
import { VitalSign, PCRForm } from '../../types/formTypes';

interface FormControlsProps {
  formData: PCRForm;
  formId: string;
  studentInfo?: { name: string; email: string } | null;
  isReadOnly?: boolean;
}

const FormControls: React.FC<FormControlsProps> = ({ studentInfo, formData, formId, isReadOnly = false }) => {
  const { saveForm, lastSaved, hasUnsavedChanges } = useForm();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleDownload = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    
    // Create a blob from the JSON data
    const blob = new Blob([dataStr], { type: 'application/json' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Setup the download
    link.href = url;
    link.download = `pcr-form-${formId}.json`;
    
    // Append to body, click to download, then remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      await saveForm();
    } catch (err) {
      console.error('Save error:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirm = window.confirm('You have unsaved changes. Are you sure you want to go back without saving?');
      if (!confirm) return;
    }
    navigate('/');
  };

  // Export PDF function
  const handleExportPDF = () => {
    // Create styles for the PDF
    const styles = {
      title: { fontSize: 20, bold: true, margin: [0, 0, 0, 10], alignment: 'center' },
      sectionHeader: { fontSize: 16, bold: true, margin: [0, 10, 0, 5], color: '#1e40af', 
                       decoration: 'underline', decorationStyle: 'solid', decorationColor: '#1e40af' },
      subSectionHeader: { fontSize: 14, bold: true, margin: [0, 5, 0, 3], color: '#374151' },
      fieldLabel: { fontSize: 12, bold: true, margin: [0, 3, 0, 2], color: '#4B5563' },
      normalText: { fontSize: 11, margin: [0, 0, 0, 5], color: '#111827' },
      vitalsTable: { margin: [0, 5, 0, 10] },
      tableHeader: { fontSize: 12, bold: true, fillColor: '#E5E7EB', color: '#1F2937' },
      tableCell: { fontSize: 11, color: '#374151' }
    };
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    let y = 15; // Starting y position
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    // Helper functions
    const addPageHeader = (): void => {
      doc.setFillColor(240, 240, 240); // Light gray background
      doc.rect(0, 0, pageWidth, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Patient Care Report (PCR)', pageWidth / 2, 6, { align: 'center' });
      y = 15;
    };

    const addTitle = (text: string): void => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175); // Indigo color
      doc.text(text, pageWidth / 2, y, { align: 'center' });
      y += 10;
    };

    const addSection = (title: string): void => {
      checkPageBreak(15);
      // Add a colored rectangle as background
      doc.setFillColor(240, 245, 255); // Light blue background
      doc.rect(margin - 5, y - 5, contentWidth + 10, 10, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(30, 64, 175); // Indigo color
      doc.text(title, margin, y);
      
      // Add a line under section
      y += 5;
      doc.setDrawColor(200, 210, 240); // Light blue line
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 7;
    };

    const addSubSection = (title: string): void => {
      checkPageBreak(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(55, 65, 81); // Gray-700
      doc.text(title, margin, y);
      y += 5;
    };

    const addField = (label: string, value: string): void => {
      checkPageBreak(15);
      
      // Add field label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(75, 85, 99); // Gray-600
      doc.text(label, margin, y);
      y += 4;
      
      // Add field value with light background
      if (value && value.trim() !== '') {
        doc.setFillColor(248, 250, 252); // Very light gray/blue
        doc.rect(margin, y - 3, contentWidth, 7, 'F');
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(17, 24, 39); // Gray-900
        
        // Handle text wrapping for longer content
        const lines = doc.splitTextToSize(value, contentWidth - 4);
        doc.text(lines, margin + 2, y);
        
        y += lines.length * 5 + 2;
      } else {
        // Empty field, just add some space
        y += 4;
      }
    };

    const addVitalsTable = (title: string, vitals: VitalSign): void => {
      checkPageBreak(50); // Check if we need a lot of space for the table
      
      addSubSection(title);
      
      // Calculate column widths
      const colWidths = [40, contentWidth - 40];
      
      // Draw the headers with a colored background
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, y - 4, colWidths[0], 8, 'F');
      doc.rect(margin + colWidths[0], y - 4, colWidths[1], 8, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(31, 41, 55); // Gray-800
      doc.text('Parameter', margin + 2, y);
      doc.text('Value', margin + colWidths[0] + 2, y);
      
      y += 6;
      
      // Function to add a row
      const addRow = (param: string, value: string | null | undefined): void => {
        checkPageBreak(8);
        
        // Alternate row background
        if ((y / 8) % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, y - 4, colWidths[0] + colWidths[1], 8, 'F');
        }
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(55, 65, 81);
        doc.text(param, margin + 2, y);
        doc.text(value || '', margin + colWidths[0] + 2, y);
        
        // Draw cell borders
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.1);
        doc.line(margin, y + 2, margin + colWidths[0] + colWidths[1], y + 2);
        
        y += 6;
      };
      
      // Add rows for each vital sign
      Object.entries(vitals).forEach(([key, value]) => {
        if (key === 'EKG' && typeof value === 'object' && value !== null) {
          const ekgValue = value as { reading?: string | null; leadType?: string | null };
          addRow('EKG', `${ekgValue.reading || ''} (${ekgValue.leadType || ''})`);
        } else if (key === 'pupils' || key === 'lungSounds') {
          addRow(key === 'pupils' ? 'Pupils' : 'Lung Sounds', 
                Array.isArray(value) ? value.join(', ') : String(value || ''));
        } else if (key !== 'time') {
          // Format the key from camelCase to a readable format
          const formattedKey = key.replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, str => str.toUpperCase());
          addRow(formattedKey, String(value || ''));
        }
      });
      
      // Add time outside the table
      doc.setFont('helvetica', 'italic');
      doc.text(`Time: ${vitals.time || 'N/A'}`, margin, y);
      y += 8;
    };
    
    const checkPageBreak = (requiredSpace = 10): void => {
      if (y + requiredSpace > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        addPageHeader();
      }
    };
    
    // Start building the PDF
    addTitle('Patient Care Report (PCR)');
    
    // Add student information if available
    if (formData.studentName || formData.studentEmail || formData.studentId) {
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(70, 70, 70);
      
      if (formData.studentName) {
        doc.text(`Student: ${formData.studentName}`, margin, y);
        y += 5;
      }
      
      if (formData.studentEmail) {
        doc.text(`Email: ${formData.studentEmail}`, margin, y);
        y += 5;
      }
      
      if (formData.studentId) {
        doc.text(`ID: ${formData.studentId}`, margin, y);
        y += 5;
      }
      
      y += 5; // Extra space after student info
    }

    if (studentInfo && (studentInfo.name || studentInfo.email)) {
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(70, 70, 70);
      
      if (studentInfo.name) {
        doc.text(`Student: ${studentInfo.name}`, margin, y);
        y += 5;
      }
      
      if (studentInfo.email) {
        doc.text(`Email: ${studentInfo.email}`, margin, y);
        y += 5;
      }
      
      y += 5; // Extra space after student info
    }

    // Dispatch Scenario
    addSection('Dispatch Scenario');
    addField('', formData.dispatchScenario || '');
    
    // Scene Size-Up
    addSection('Scene Size-Up');
    addSubSection('BSI');
    addField('', formData.sceneSizeUp.BSI || '');
    
    addSubSection('Nature of Illness');
    addField('', formData.sceneSizeUp.natureOfIllness || '');
    
    addSubSection('Additional Resources');
    addField('', formData.sceneSizeUp.additionalResources || '');
    
    addSubSection('Spinal Precautions');
    addField('', formData.sceneSizeUp.spinalPrecautions || '');
    
    // Primary Assessment
    addSection('Primary Assessment');
    addSubSection('General Impression');
    addField('', formData.primaryAssessment.generalImpression || '');
    
    addSubSection('LOC + Mental Status');
    addField('AVPU', formData.primaryAssessment.LOCMentalStatus.AVPU || '');
    addField('Orientation', formData.primaryAssessment.LOCMentalStatus.orientation?.join(", ") || "");
    
    addSubSection('Chief Complaint');
    addField('', formData.primaryAssessment.chiefComplaint || '');
    
    addSubSection('Airway Assessment');
    addField('', formData.primaryAssessment.airwayAssessment || '');
    
    addSubSection('Breathing Assessment');
    addField('', formData.primaryAssessment.breathingAssessment || '');
    
    addSubSection('Circulation Assessment');
    addField('Skin Color', formData.primaryAssessment.circulationAssessment.skin.color || '');
    addField('Skin Temperature', formData.primaryAssessment.circulationAssessment.skin.temperature || '');
    addField('Skin Condition', formData.primaryAssessment.circulationAssessment.skin.condition || '');
    addField('Pulse Rate', formData.primaryAssessment.circulationAssessment.pulse.rate || '');
    addField('Pulse Rhythm', formData.primaryAssessment.circulationAssessment.pulse.rhythm || '');
    addField('Pulse Quality', formData.primaryAssessment.circulationAssessment.pulse.quality || '');
    
    addSubSection('GCS');
    addField('Eye Opening', formData.primaryAssessment.GCS.eyeOpening?.toString() || '0');
    addField('Verbal Response', formData.primaryAssessment.GCS.verbalResponse?.toString() || '0');
    addField('Motor Response', formData.primaryAssessment.GCS.motorResponse?.toString() || '0');
    addField('Total Score', formData.primaryAssessment.GCS.totalScore?.toString() || '0');
    
    addSubSection('Decision (Pt. Priority)');
    addField('', formData.primaryAssessment.patientPriority || '');
    
    // Differential Diagnosis
    addSection('Differential Diagnosis');
    addSubSection('Pathway 1');
    addField('', formData.differentialDiagnosis.pathway1 || '');
    
    addSubSection('Pathway 2');
    addField('', formData.differentialDiagnosis.pathway2 || '');
    
    addSubSection('Pathway 3');
    addField('', formData.differentialDiagnosis.pathway3 || '');

    // Vital Signs
    addSection('Vital Signs');
    
    // Add vital signs tables - using type assertion if needed
    addVitalsTable('Initial Vitals', formData.vitalSigns.sets[0] as VitalSign);
    addVitalsTable('2nd Set', formData.vitalSigns.sets[1] as VitalSign);
    addVitalsTable('3rd Set', formData.vitalSigns.sets[2] as VitalSign);
    
    addSubSection('Reassessment');
    addField('', formData.vitalSigns.reassessment || '');
    
    // Pathophysiology
    addSection('Pathophysiology');
    addSubSection('Pathway 1');
    addField('', formData.pathophysiology.pathway1 || '');
    
    addSubSection('Pathway 2');
    addField('', formData.pathophysiology.pathway2 || '');
    
    addSubSection('Pathway 3');
    addField('', formData.pathophysiology.pathway3 || '');
    
    // Field Diagnosis
    addSection('Field Diagnosis');
    addSubSection('Step 1');
    addField('', formData.fieldDiagnosis.step1 || '');
    
    addSubSection('Step 2');
    addField('', formData.fieldDiagnosis.step2 || '');
    
    addSubSection('Step 3');
    addField('', formData.fieldDiagnosis.step3 || '');
    
    addSubSection('Treatment & Interventions');
    addField('', formData.fieldDiagnosis.treatmentAndInterventions || '');
    
    // Documentation
    addSection('Documentation');
    addSubSection('Narrative');
    addField('', formData.documentation.narrative || '');
    
    addSubSection('Radio Report');
    addField('', formData.documentation.radioReport || '');
    
    addSubSection('Drug Cards');
    addField('', formData.documentation.drugCards || '');
    
    addSubSection('Format + Resources');
    addField('', formData.documentation.formatAndResources || '');
    
    // Add footer with page numbers
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10);
      
      // Add student name in footer if available
      if (formData.studentName) {
        doc.text(`Student: ${formData.studentName}`, margin, doc.internal.pageSize.getHeight() - 10);
      }
    }
    
    // Create filename with student name if available
    let filename = 'PCR-Form';
    if (studentInfo && studentInfo.name) {
      filename = `PCR-Form-${studentInfo.name.replace(/\s+/g, '_')}`;
    }
    
    // Save the PDF
    doc.save(`${filename}.pdf`);
  };

  // Export Text function
  const handleExportText = () => {
    let textContent = 'PATIENT CARE REPORT (PCR)\n\n';
    
    // Add student information if available
    if (studentInfo && (studentInfo.name || studentInfo.email)) {
      textContent += '============ STUDENT INFORMATION ============\n';
      if (studentInfo.name) {
        textContent += `Student Name: ${studentInfo.name}\n`;
      }
      if (studentInfo.email) {
        textContent += `Student Email: ${studentInfo.email}\n`;
      }
      textContent += '\n';
    }
    
    // Dispatch Scenario
    textContent += '============ DISPATCH SCENARIO ============\n';
    textContent += formData.dispatchScenario || '' + '\n\n';
    
    // Scene Size-Up
    textContent += '============ SCENE SIZE-UP ============\n';
    textContent += 'BSI:\n' + (formData.sceneSizeUp.BSI || '') + '\n\n';
    textContent += 'Nature of Illness:\n' + (formData.sceneSizeUp.natureOfIllness || '') + '\n\n';
    textContent += 'Additional Resources:\n' + (formData.sceneSizeUp.additionalResources || '') + '\n\n';
    textContent += 'Spinal Precautions:\n' + (formData.sceneSizeUp.spinalPrecautions || '') + '\n\n';
    
    // Primary Assessment
    textContent += '============ PRIMARY ASSESSMENT ============\n';
    textContent += 'General Impression:\n' + (formData.primaryAssessment.generalImpression || '') + '\n\n';
    
    textContent += 'LOC + Mental Status:\n';
    textContent += 'AVPU: ' + (formData.primaryAssessment.LOCMentalStatus.AVPU || '') + '\n';
    textContent += 'Orientation: ' + (formData.primaryAssessment.LOCMentalStatus.orientation?.join(", ") || "") + '\n\n';
    
    textContent += 'Chief Complaint:\n' + (formData.primaryAssessment.chiefComplaint || '') + '\n\n';
    textContent += 'Airway Assessment:\n' + (formData.primaryAssessment.airwayAssessment || '') + '\n\n';
    textContent += 'Breathing Assessment:\n' + (formData.primaryAssessment.breathingAssessment || '') + '\n\n';
    
    textContent += 'Circulation Assessment:\n';
    textContent += 'Skin Color: ' + (formData.primaryAssessment.circulationAssessment.skin.color || '') + '\n';
    textContent += 'Skin Temperature: ' + (formData.primaryAssessment.circulationAssessment.skin.temperature || '') + '\n';
    textContent += 'Skin Condition: ' + (formData.primaryAssessment.circulationAssessment.skin.condition || '') + '\n';
    textContent += 'Pulse Rate: ' + (formData.primaryAssessment.circulationAssessment.pulse.rate || '') + '\n';
    textContent += 'Pulse Rhythm: ' + (formData.primaryAssessment.circulationAssessment.pulse.rhythm || '') + '\n';
    textContent += 'Pulse Quality: ' + (formData.primaryAssessment.circulationAssessment.pulse.quality || '') + '\n\n';
    
    textContent += 'GCS:\n';
    textContent += 'Eye Opening: ' + (formData.primaryAssessment.GCS.eyeOpening || 0) + '\n';
    textContent += 'Verbal Response: ' + (formData.primaryAssessment.GCS.verbalResponse || 0) + '\n';
    textContent += 'Motor Response: ' + (formData.primaryAssessment.GCS.motorResponse || 0) + '\n';
    textContent += 'Total Score: ' + (formData.primaryAssessment.GCS.totalScore || 0) + '\n\n';
    
    textContent += 'Decision (Pt. Priority):\n' + (formData.primaryAssessment.patientPriority || '') + '\n\n';
    
    // Differential Diagnosis
    textContent += '============ DIFFERENTIAL DIAGNOSIS ============\n';
    textContent += 'Pathway 1:\n' + (formData.differentialDiagnosis.pathway1 || '') + '\n\n';
    textContent += 'Pathway 2:\n' + (formData.differentialDiagnosis.pathway2 || '') + '\n\n';
    textContent += 'Pathway 3:\n' + (formData.differentialDiagnosis.pathway3 || '') + '\n\n';
    
    // Vital Signs
    textContent += '============ VITAL SIGNS ============\n';
    
    // Initial Vitals
    textContent += 'Initial Vitals:\n';
    textContent += 'Time: ' + (formData.vitalSigns.sets[0].time || '') + '\n';
    textContent += 'Blood Pressure: ' + (formData.vitalSigns.sets[0].bloodPressure || '') + '\n';
    textContent += 'Heart Rate: ' + (formData.vitalSigns.sets[0].heartRate || '') + '\n';
    textContent += 'Respiratory Rate: ' + (formData.vitalSigns.sets[0].respiratoryRate || '') + '\n';
    textContent += 'SpO2: ' + (formData.vitalSigns.sets[0].spO2 || '') + '\n';
    textContent += 'BGL: ' + (formData.vitalSigns.sets[0].BGL || '') + '\n';
    textContent += 'IV Gauge: ' + (formData.vitalSigns.sets[0].IVGauge || '') + '\n';
    textContent += 'EKG: ' + (formData.vitalSigns.sets[0].EKG.reading || '') + ' (' + (formData.vitalSigns.sets[0].EKG.leadType || '') + ')\n';
    textContent += 'Pupils: ' + (formData.vitalSigns.sets[0].pupils.join(", ") || '') + '\n';
    textContent += 'Lung Sounds: ' + (formData.vitalSigns.sets[0].lungSounds.join(", ") || '') + '\n\n';
    
    // 2nd Set
    textContent += '2nd Set:\n';
    textContent += 'Time: ' + (formData.vitalSigns.sets[1].time || '') + '\n';
    textContent += 'Blood Pressure: ' + (formData.vitalSigns.sets[1].bloodPressure || '') + '\n';
    textContent += 'Heart Rate: ' + (formData.vitalSigns.sets[1].heartRate || '') + '\n';
    textContent += 'Respiratory Rate: ' + (formData.vitalSigns.sets[1].respiratoryRate || '') + '\n';
    textContent += 'SpO2: ' + (formData.vitalSigns.sets[1].spO2 || '') + '\n';
    textContent += 'BGL: ' + (formData.vitalSigns.sets[1].BGL || '') + '\n';
    textContent += 'IV Gauge: ' + (formData.vitalSigns.sets[1].IVGauge || '') + '\n';
    textContent += 'EKG: ' + (formData.vitalSigns.sets[1].EKG.reading || '') + ' (' + (formData.vitalSigns.sets[1].EKG.leadType || '') + ')\n';
    textContent += 'Pupils: ' + (formData.vitalSigns.sets[1].pupils.join(", ") || '') + '\n';
    textContent += 'Lung Sounds: ' + (formData.vitalSigns.sets[1].lungSounds.join(", ") || '') + '\n\n';
    
    // 3rd Set
    textContent += '3rd Set:\n';
    textContent += 'Time: ' + (formData.vitalSigns.sets[2].time || '') + '\n';
    textContent += 'Blood Pressure: ' + (formData.vitalSigns.sets[2].bloodPressure || '') + '\n';
    textContent += 'Heart Rate: ' + (formData.vitalSigns.sets[2].heartRate || '') + '\n';
    textContent += 'Respiratory Rate: ' + (formData.vitalSigns.sets[2].respiratoryRate || '') + '\n';
    textContent += 'SpO2: ' + (formData.vitalSigns.sets[2].spO2 || '') + '\n';
    textContent += 'BGL: ' + (formData.vitalSigns.sets[2].BGL || '') + '\n';
    textContent += 'IV Gauge: ' + (formData.vitalSigns.sets[2].IVGauge || '') + '\n';
    textContent += 'EKG: ' + (formData.vitalSigns.sets[2].EKG.reading || '') + ' (' + (formData.vitalSigns.sets[2].EKG.leadType || '') + ')\n';
    textContent += 'Pupils: ' + (formData.vitalSigns.sets[2].pupils.join(", ") || '') + '\n';
    textContent += 'Lung Sounds: ' + (formData.vitalSigns.sets[2].lungSounds.join(", ") || '') + '\n\n';
    
    textContent += 'Reassessment:\n' + (formData.vitalSigns.reassessment || '') + '\n\n';
    
    // Pathophysiology
    textContent += '============ PATHOPHYSIOLOGY ============\n';
    textContent += 'Pathway 1:\n' + (formData.pathophysiology.pathway1 || '') + '\n\n';
    textContent += 'Pathway 2:\n' + (formData.pathophysiology.pathway2 || '') + '\n\n';
    textContent += 'Pathway 3:\n' + (formData.pathophysiology.pathway3 || '') + '\n\n';
    
    // Field Diagnosis
    textContent += '============ FIELD DIAGNOSIS ============\n';
    textContent += 'Step 1:\n' + (formData.fieldDiagnosis.step1 || '') + '\n\n';
    textContent += 'Step 2:\n' + (formData.fieldDiagnosis.step2 || '') + '\n\n';
    textContent += 'Step 3:\n' + (formData.fieldDiagnosis.step3 || '') + '\n\n';
    textContent += 'Treatment & Interventions:\n' + (formData.fieldDiagnosis.treatmentAndInterventions || '') + '\n\n';
    
    // Documentation
    textContent += '============ DOCUMENTATION ============\n';
    textContent += 'Narrative:\n' + (formData.documentation.narrative || '') + '\n\n';
    textContent += 'Radio Report:\n' + (formData.documentation.radioReport || '') + '\n\n';
    textContent += 'Drug Cards:\n' + (formData.documentation.drugCards || '') + '\n\n';
    textContent += 'Format + Resources:\n' + (formData.documentation.formatAndResources || '') + '\n\n';
    
    // Create blob and save file
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    // Create filename with student name if available
    let filename = 'PCR-Form';
    if (formData.studentName) {
      filename = `PCR-Form-${formData.studentName.replace(/\s+/g, '_')}`;
    }
    
    link.download = `${filename}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  
  const formatDate = (date: Date | null) => {
    if (!date) return 'Not saved yet';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // If in read-only mode, don't render the control bar at all
  if (isReadOnly) {
    return null;
  }
  
  // Only render the control bar for editing mode
  return (
    <div className="bg-white shadow-md rounded-md p-4 fixed bottom-4 left-0 right-0 z-50 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
            className={`${
              hasUnsavedChanges 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-gray-400 cursor-not-allowed'
            } text-white py-2 px-4 rounded-md flex items-center space-x-1 transition-colors`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h1a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h1v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                </svg>
                <span>Save</span>
              </>
            )}
          </button>
  
          {saveError ? (
            <span className="text-red-600 text-sm animate-pulse">
              {saveError}
            </span>
          ) : (
            <span className={`text-sm ${hasUnsavedChanges ? 'text-yellow-600' : 'text-green-600'}`}>
              {hasUnsavedChanges 
                ? 'Unsaved changes' 
                : `Last saved: ${formatDate(lastSaved)}`}
            </span>
          )}
        </div>
        
        <div className="flex space-x-2">
          <button 
            onClick={handleExportPDF}
            className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md"
          >
            Export as PDF
          </button>
          <button 
            onClick={handleExportText}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
          >
            Export Text
          </button>
          <button 
            onClick={handleBack}
            className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded-md text-sm"
          >
            Back
          </button>
        </div>
      </div>
      
      <div className="text-xs text-gray-500 mt-2">
        User ID: {formData.userId}
        {studentInfo && studentInfo.name && ` | Student: ${studentInfo.name}`}
      </div>
    </div>
  );
};

export default FormControls;